/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose, Query } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'

import {
  Table,
  SvgIcon,
  Spinner,
  PageNavigationButtons,
  Warning,
  Button,
  DeductionIdentityPaymentsEdit
} from '../'

import {
  showAsCurrency,
  PageNavigationControl,
  decodeBase64,
  getPageCursor
} from '../../utils'

const QUERY_IDENTITY_PAYMENTS = gql`
  query($id: ID!, $parentPayment: UUID!) {
    identity(id: $id) {
      id
      firstName
      lastName
      identityValue
      client {
        id
        employeeNumber
      }
      paymentsFor(transactedIn_Id: $parentPayment) {
        totalCount
        edges {
          node {
            id
            amount
          }
        }
      }
    }
  }
`

class PayrollRunClientPayments extends React.PureComponent {
  static propTypes = {
    payrollCycleId: PropTypes.string.isRequired,
    workflowRunDeductingId: PropTypes.string.isRequired,
    parentPaymentId: PropTypes.string.isRequired,
    employmentType: PropTypes.string,
    resultsPerPage: PropTypes.number,
    readOnly: PropTypes.bool,
    className: PropTypes.string
  }

  static defaultProps = {
    resultsPerPage: 15,
    className: ''
  }

  state = {
    refetch: false,
    pageNav: new PageNavigationControl(),
    editIdentity: null,
    viewedIdentity: null,
    errorOccurredDuringPaymentSave: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.error && !this.state.refetch) {
      this.setState({ refetch: true })
      nextProps.refetch()
    }

    if (this.state.refetch) {
      this.setState({ refetch: false })
    }

    if (nextProps.identities) {
      this.state.pageNav.setPageStates(this.props.identities, nextProps.identities)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.loading) {
      // NOTE: 3rd arg can be passed to setPageStates if page state (PageNavigation first/last/current page & pages) needs to be saved
      this.state.pageNav.setPageStates(prevProps.identities, this.props.identities)
    }
  }

  render() {
    if (this.props.loading) {
      return <Spinner type='brand' className='slds-is-relative' />
    }

    if (this.props.error) {
      return (
        <Warning className={this.props.className ? this.props.className : ''} iconSize='medium' iconMargin='medium'>
          Error loading payment data.
        </Warning>
      )
    }

    if (this.props.identities.edges.length === 0) {
      return (
        <div className={`slds-m-left_large ${this.props.className}`}>
          <SvgIcon
            variant='utility'
            symbol='info'
            colour='light'
            size='small'
            className='slds-m-right_small'
          />
          No identity records to display.
        </div>
      )
    }

    let pageNav = this.state.pageNav
    let startCursor = this.props.identities.pageInfo.startCursor
    let totalCount = this.props.identities.totalCount

    return (
      <div className={`slds-scrollable_x ${this.props.className ? this.props.className : ''}`}>
        <Table
          columns={[ 'Name', 'Surname', 'ID', 'Empl#', 'Total', 'Change', '' ]}
          rows={this.props.identities.edges.map(({ node: identity }, index) => {
            if (this.state.editIdentity === identity.id) {
              return (
                <tr className="slds-theme_shade">
                  <td colSpan={7}>
                    <DeductionIdentityPaymentsEdit
                      deductionId={this.props.workflowRunDeductingId}
                      payrollCycleId={this.props.payrollCycleId}
                      identityId={identity.id}
                      identityForName={identity.fullName}
                      onSave={(identityId, errorOccurredDuringPaymentSave) => {
                        if (errorOccurredDuringPaymentSave) {
                          this.setState({
                            viewedIdentity: identityId,
                            errorOccurredDuringPaymentSave: true
                          })
                        } else {
                          this.setState({ viewedIdentity: identityId })
                        }
                      }}
                      viewMode={identityId => {
                        this.setState({
                          editIdentity: null,
                          viewedIdentity: identityId
                        })
                      }}
                    />
                  </td>
                </tr>
              )
            } else {
              return (
                <PaymentDetailsRow
                  identityId={identity.id}
                  parentPaymentId={this.props.parentPaymentId}
                  onEditClick={identityId => this.setState({
                    editIdentity: identityId,
                    viewedIdentity: null
                  })}
                  updateViewedIdentity={identityId => this.setState({ viewedIdentity: identityId })}
                  viewedIdentity={this.state.viewedIdentity}
                  errorOccurredDuringPaymentSave={this.state.errorOccurredDuringPaymentSave}
                  readOnly={this.props.readOnly}
                  key={`paymentDetailsRow_${index}`}
                />
              )
            }
          })}
          objectRows={true}
        />
        <PageNavigationButtons
          onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
          onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
          previousDisabled={pageNav.firstPage}
          nextDisabled={pageNav.lastPage}
          startCursor={startCursor}
          totalCount={totalCount}
          resultsPerPage={this.props.resultsPerPage}
          resultsText='payments'
          onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
          className="slds-p-around_medium"
        />
      </div>
    )
  }
}

const PaymentDetailsRow = ({ identityId, parentPaymentId, onEditClick, updateViewedIdentity,
  viewedIdentity, errorOccurredDuringPaymentSave, readOnly }) => (
  <Query query={QUERY_IDENTITY_PAYMENTS} variables={{ id: identityId, parentPayment: decodeBase64(parentPaymentId) }}>
    {({ loading, error, data }) => {
      if (loading) {
        return <Stencil backgroundColour='lightgrey' />
      }

      if (error) {
        return <Stencil backgroundColour='indianred' />
      }

      let identityPaymentsTotal = data.identity.paymentsFor.edges.reduce(
        (total, payment) => total + parseFloat(payment.node.amount),
        0
      )
      let identityPaymentChange = data.identity.paymentsFor.edges.find(({ node: payment }) => (
        payment.state === 'NEW' || payment.state === 'CHANGED'
      ))

      let editButton = data.identity && data.identity.paymentsFor.edges.length > 0  // does identity have current pending deduction payments? (won't have if terminated & have removed payments)
        ? <Button
            type='reset'
            disabled={readOnly}
            onClick={() => onEditClick(data.identity.id)}>
            <SvgIcon
              variant='utility'
              symbol='edit'
              size='x-small'
              colour={readOnly ? 'light' : 'default'}
              buttonIcon={true}
            />
          </Button>
        : ''

      let identity = data.identity
      return (
        <tr
          className={`slds-border_top ${viewedIdentity === identity.id ? ` deduction-payments__viewed${errorOccurredDuringPaymentSave ? '_error' : ''}` : ''}`}
          key={`${identity.id}_payment`}
          >
          <td data-label='Name'>
            {identity.firstName}
          </td>
          <td data-label='Surname'>
            {identity.lastName}
          </td>
          <td data-label='ID'>
            {identity.identityValue}
          </td>
          <td data-label='Empl#'>
            {identity.client && identity.client.employeeNumber
              ? identity.client.employeeNumber
              : '-'
            }
          </td>
          <td data-label='Total'>
            {showAsCurrency('R', identityPaymentsTotal, 2)}
          </td>
          <td data-label='Change'>
            {identityPaymentChange
              ? '&#10004;'  // or 10003 represents check character
              : '-'
            }
          </td>
          <td data-label=''>
            {editButton}
          </td>
        </tr>
      )
    }}
  </Query>
)

const Stencil = ({ backgroundColour }) => (
  <tr style={{
    width: '100%',
    height: '40px',
    borderRadius: '6px',
    boxShadow: '0 5px 20px rgba(0,0,0, .1)'
  }}>
    <td style={{ width: '12%' }}>
      <div style={{
        padding: '2px',
        backgroundColor: backgroundColour
      }}>
      </div>
    </td>
    <td style={{ width: '12%' }}>
      <div style={{
        padding: '2px',
        backgroundColor: backgroundColour
      }}>
      </div>
    </td>
    <td style={{ width: '12%' }}>
      <div style={{
        padding: '2px',
        backgroundColor: backgroundColour
      }}>
      </div>
    </td>
    <td style={{ width: '12%' }}>
      <div style={{
        padding: '2px',
        backgroundColor: backgroundColour
      }}>
      </div>
    </td>
    <td style={{ width: '12%' }}>
      <div style={{
        padding: '2px',
        backgroundColor: backgroundColour
      }}>
      </div>
    </td>
    <td style={{ width: '12%' }}>
      <div style={{
        padding: '2px',
        backgroundColor: backgroundColour
      }}>
      </div>
    </td>
    <td style={{ width: '12%' }}>
      <div style={{
        padding: '2px',
        backgroundColor: backgroundColour
      }}>
      </div>
    </td>
  </tr>
)

const getPayrollRunClients = gql`
  query($parentPayment: UUID!, $employmentType: String, $searchString: String, $numberOfRecordsPerPage: Int!, $cursor: String) {
    identities(paymentsFor_TransactedIn: $parentPayment, client_EmploymentType: $employmentType, search: $searchString, first: $numberOfRecordsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          fullName
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousPayrollRunClients = gql`
  query($parentPayment: UUID!, $employmentType: String, $searchString: String, $numberOfRecordsPerPage: Int!, $cursor: String) {
    identities(paymentsFor_TransactedIn: $parentPayment, client_EmploymentType: $employmentType, search: $searchString, last: $numberOfRecordsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          fullName
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData = graphql(getPayrollRunClients, {
    options: ({ parentPaymentId, employmentType, searchString, numberOfRecordsPerPage = 15, cursor }) => (
      {
        variables: {
          parentPayment: decodeBase64(parentPaymentId),
          employmentType,
          searchString,
          numberOfRecordsPerPage,
          cursor
        },
        fetchPolicy: 'network-only'
      }
    ),
    props: ({ ownProps: { parentPaymentId, employmentType, searchString, numberOfRecordsPerPage = 15 }, data: { loading, error, refetch, identities, fetchMore } }) => {
      return {
        loading,
        error,
        refetch,
        identities,
        fetchNext: () => {
          return fetchMore({
            query: getPayrollRunClients,
            variables: {
              parentPayment: decodeBase64(parentPaymentId),
              employmentType,
              searchString,
              numberOfRecordsPerPage,
              cursor: identities.pageInfo.endCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const newEdges = fetchMoreResult.identities.edges
              const pageInfo = fetchMoreResult.identities.pageInfo
              return {
                identities: {
                  __typename: previousResult.identities.__typename,
                  edges: [...newEdges],
                  pageInfo
                }
              }
            }
          })
        },
        fetchPrevious: () => {
          return fetchMore({
            query: getPreviousPayrollRunClients,
            variables: {
              parentPayment: decodeBase64(parentPaymentId),
              employmentType,
              searchString,
              numberOfRecordsPerPage,
              cursor: identities.pageInfo.startCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const newEdges = fetchMoreResult.identities.edges
              const pageInfo = fetchMoreResult.identities.pageInfo
              return {
                identities: {
                  __typename: previousResult.identities.__typename,
                  edges: [...newEdges],
                  pageInfo
                }
              }
            }
          })
        }
      }
    }
  })

export default compose(
  withData,
  withRouter
)(PayrollRunClientPayments)



// WEBPACK FOOTER //
// ./src/components/payroll-run-client-payments/index.js